import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormHelperText,
  Box,
  MenuItem,
  CircularProgress,
  Select,
  Checkbox,
  ListItemText,
  FormControl,
  InputLabel,
} from "@mui/material";
import { useFormik } from "formik";
import * as yup from "yup";
import { makeStyles } from "tss-react/mui";
import { useSelector } from "react-redux";

const useStyles = makeStyles()((theme, _params, classes) => ({
  gradientText: {
    background: theme.palette.accentGradient,
    backgroundClip: "text",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
  },
}));

const validationSchema = yup.object({
  name: yup.string().trim().required("Name is required."),
  email: yup
    .string()
    .trim()
    .email("Invalid email address.")
    .required("Email is required."),
  role: yup.string().trim().required("Role is required."),
  // plant: yup.string().trim().required("Plant is required."),
  // line: yup.string().trim().required("Line is required."),
});

function EditUserDialog({ user, handleUpdateUser, onClose, roles }) {
  const [isLoading, setIsLoading] = useState(false);
  const { classes } = useStyles();
  const initialValues = {
    name: user["name"],
    email: user["email"],
    role: user["role"],
    access_domains: user["access_domains"]?.domains || [],
    line: user["user_info"]?.line || "",
  };
  const { domains } = useSelector((state) => state.domain);

  async function onSubmit(values, { resetForm }) {
    try {
      setIsLoading(true);
      await handleUpdateUser(user["id"], {
        name: values["name"],
        email: values["email"],
        role: values["role"],
        access_domains: {
          domains: values["access_domains"],
        },
        user_info: {
          ...user["user_info"],
          line: values["line"],
        },
      });
      onClose();
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  }

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: initialValues,
    validationSchema: validationSchema,
    onSubmit,
  });

  return (
    <Dialog
      open={true}
      onClose={onClose}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      fullWidth
      PaperProps={{
        style: {
          maxWidth: "sm",
        },
      }}>
      <DialogTitle variant="h6" sx={{ display: "flex", alignItems: "center" }}>
        Edit User
      </DialogTitle>
      <DialogContent>
        <form>
          <TextField
            fullWidth
            sx={{ marginTop: 1 }}
            variant="outlined"
            label="Name"
            name="name"
            error={Boolean(formik.errors.name)}
            value={formik.values.name}
            onChange={formik.handleChange}
          />
          {formik.errors.name && (
            <FormHelperText error sx={{ ml: 1 }}>
              {formik.errors.name}
            </FormHelperText>
          )}
          <Box mb={3} />
          <TextField
            fullWidth
            variant="outlined"
            label="Email"
            name="email"
            error={Boolean(formik.errors.email)}
            value={formik.values.email}
            onChange={formik.handleChange}
          />
          {formik.errors.email && (
            <FormHelperText error sx={{ ml: 1 }}>
              {formik.errors.email}
            </FormHelperText>
          )}
          <Box mb={3} />
          <TextField
            fullWidth
            variant="outlined"
            label="Role"
            name="role"
            error={Boolean(formik.errors.role)}
            value={formik.values.role}
            onChange={formik.handleChange}
            select>
            {roles.map((role) => (
              <MenuItem key={role.role} value={role.role}>
                {role.role}
              </MenuItem>
            ))}
          </TextField>
          {formik.errors.role && (
            <FormHelperText error sx={{ ml: 1 }}>
              {formik.errors.role}
            </FormHelperText>
          )}
          <Box mb={3} />
          <FormControl fullWidth>
            <InputLabel id="demo-multiple-checkbox-label">Plant</InputLabel>
            <Select
              labelId="demo-multiple-checkbox-label"
              multiple
              fullWidth
              variant="outlined"
              label="Plant"
              name="access_domains"
              error={Boolean(formik.errors.access_domains)}
              value={formik.values.access_domains}
              onChange={formik.handleChange}
              renderValue={(selected) =>
                domains
                  .filter((d) => selected.includes(d.id))
                  .map((d) => d.domain)
                  .join(", ")
              }>
              {domains.map((domain) => (
                <MenuItem key={domain.id} value={domain.id}>
                  <Checkbox
                    checked={formik.values.access_domains.includes(domain.id)}
                  />
                  <ListItemText>{domain.domain}</ListItemText>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {formik.errors.access_domains && (
            <FormHelperText error sx={{ ml: 1 }}>
              {formik.errors.access_domains}
            </FormHelperText>
          )}
          <Box mb={3} />
          <TextField
            fullWidth
            variant="outlined"
            label="Line"
            name="line"
            error={Boolean(formik.errors.line)}
            value={formik.values.line}
            onChange={formik.handleChange}
          />
          {formik.errors.line && (
            <FormHelperText error sx={{ ml: 1 }}>
              {formik.errors.line}
            </FormHelperText>
          )}
        </form>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary">
          <span className={classes.gradientText}>Cancel</span>
        </Button>
        <Button
          onClick={formik.handleSubmit}
          variant="contained"
          color="primary"
          disabled={
            formik.isSubmitting || !formik.dirty || !formik.isValid || isLoading
          }>
          {isLoading ? <CircularProgress size={20} /> : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default EditUserDialog;
